import React, { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import SoaDocument from "@/components/soa/SoaDocument";
import LumpSumDocument from "@/components/claims/LumpSumDocument";
import ClaimSummaryDocument from "@/components/claims/ClaimSummaryDocument";
import WorkerInvoiceDocument from "@/components/freelance/WorkerInvoiceDocument";
import { lumpsumFilename } from "@/lib/lumpsum";
import QuoteDocument, { StatementDocument } from "@/components/QuoteDocument";
import { pdfFilename } from "@/lib/quoteDoc";
import { whatsappUrl } from "@/lib/share";

import { A4_WIDTH_PX } from "@/components/QuoteDocument";

const nextFrame = () => new Promise((r) => setTimeout(r, 120));
// html2canvas snapshots immediately — make sure every image in the document (logo, PayNow QR) has finished loading first
const imagesReady = (root) => Promise.all([...(root?.querySelectorAll("img") || [])].map((img) => (img.complete ? Promise.resolve() : new Promise((res) => { img.onload = img.onerror = res; setTimeout(res, 4000); }))));

const PDF_OPTS = (filename) => ({
      margin: 0,
      filename,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, windowWidth: A4_WIDTH_PX },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
});

async function renderPdf(el, filename) {
  const { default: html2pdf } = await import("html2pdf.js");
  await html2pdf().set(PDF_OPTS(filename)).from(el).save();
}

async function pdfFile(el, filename) {
  const { default: html2pdf } = await import("html2pdf.js");
  const blob = await html2pdf().set(PDF_OPTS(filename)).from(el).outputPdf("blob");
  return new File([blob], filename, { type: "application/pdf" });
}

const downloadFile = (file) => {
  const url = URL.createObjectURL(file);
  Object.assign(document.createElement("a"), { href: url, download: file.name }).click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// Renders a hidden QuoteDocument and exposes print / PDF actions for any quote doc
export function useQuotePrinter() {
  const [doc, setDoc] = useState(null);
  const ref = useRef(null);
  const busy = useRef(false);

  const withDoc = useCallback(async (nextDoc, action) => {
    if (busy.current) { toast.info("Still generating the previous document — one moment"); throw new Error("Document generation already in progress"); }
    busy.current = true;
    setDoc(nextDoc);
    await nextFrame();
    await imagesReady(ref.current || document.getElementById("print-quote"));
    try {
      await action();
    } finally {
      busy.current = false;
    }
  }, []);

  const exportPdf = useCallback((nextDoc) => withDoc(nextDoc, async () => {
    const id = toast.loading("Generating PDF… 生成中");
    try {
      const name = nextDoc.filename || (nextDoc.kind === "lumpsum" ? lumpsumFilename(nextDoc.inv) : pdfFilename(nextDoc));
      await renderPdf(ref.current, name);
      toast.success("PDF downloaded", { id, description: name });
    } catch (e) {
      toast.error("PDF failed", { id, description: String(e?.message || e) });
    }
  }), [withDoc]);

  // WhatsApp + PDF: native share sheet (phone/tablet) with the PDF attached; on desktop downloads the PDF and opens WhatsApp with the text
  const sharePdf = useCallback((nextDoc, { text, phone, countryCode, filename }) => withDoc(nextDoc, async () => {
    const id = toast.loading("Preparing PDF… 生成中");
    try {
      const file = await pdfFile(ref.current, filename || pdfFilename(nextDoc));
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        toast.dismiss(id);
        const shareFile = async () => { try { await navigator.share({ files: [file], title: file.name }); } catch (err) { if (err?.name !== "AbortError") toast.error("Share failed", { description: String(err?.message || err) }); } };
        // Android WhatsApp drops the wording when a file is attached (iOS keeps it) → send the message first, then attach the PDF
        if (/Android/i.test(navigator.userAgent) && text) {
          window.open(whatsappUrl(phone, countryCode, text), "_blank", "noopener");
          toast("Step 2 · attach the PDF 附上PDF", {
            duration: 60000, id: "android-pdf-step",
            description: "Message opened in WhatsApp. Once sent, come back and tap to share the PDF.",
            action: { label: "Attach PDF", onClick: shareFile },
          });
          return;
        }
        try { await navigator.share({ files: [file], text, title: file.name }); } catch (err) { if (err?.name !== "AbortError") toast.error("Share failed", { description: String(err?.message || err) }); }
        return;
      }
      downloadFile(file);
      window.open(whatsappUrl(phone, countryCode, text), "_blank", "noopener");
      toast.success("PDF downloaded — attach it in WhatsApp", { id, description: `${file.name} · message opened in WhatsApp` });
    } catch (e) {
      toast.error("Could not prepare PDF", { id, description: String(e?.message || e) });
    }
  }), [withDoc]);

  const print = useCallback((nextDoc) => withDoc(nextDoc, async () => {
    window.print();
  }), [withDoc]);

  // Returns the PDF as a base64 string (for emailing) plus its filename
  const pdfBase64 = useCallback((nextDoc) => new Promise((resolve, reject) => withDoc(nextDoc, async () => {
    try {
      const file = await pdfFile(ref.current, nextDoc.filename || pdfFilename(nextDoc));
      const reader = new FileReader();
      reader.onload = () => resolve({ base64: String(reader.result).split(",")[1], filename: file.name });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } catch (e) { reject(e); }
  }).catch(reject)), [withDoc]);

  // Several documents in one PDF (one per page)
  const exportMany = useCallback((docs, filename) => withDoc({ kind: "bundle", docs }, async () => {
    const id = toast.loading(`Generating ${docs.length}-page PDF…`);
    try {
      await renderPdf(ref.current, filename);
      toast.success("PDF downloaded", { id, description: filename });
    } catch (e) {
      toast.error("PDF failed", { id, description: String(e?.message || e) });
    }
  }), [withDoc]);

  // Several documents rendered to one PDF File (not downloaded) — for server-side merging
  const pdfFileMany = useCallback((docs, filename) => new Promise((resolve, reject) => withDoc({ kind: "bundle", docs }, async () => {
    try { resolve(await pdfFile(ref.current, filename)); } catch (e) { reject(e); }
  }).catch(reject)), [withDoc]);

  const renderDoc = (d, r) => (d?.kind === "soa" ? <SoaDocument ref={r} soa={d.soa} shop={d.shop} /> : d?.kind === "lumpsum" ? <LumpSumDocument ref={r} inv={d.inv} /> : d?.kind === "claim_summary" ? <ClaimSummaryDocument ref={r} doc={d} /> : d?.kind === "worker_invoice" ? <WorkerInvoiceDocument ref={r} inv={d.inv} /> : d?.kind === "statement" ? <StatementDocument ref={r} doc={d} /> : <QuoteDocument ref={r} doc={d} />);
  const portal = <div id="print-quote" className="print-doc">{doc?.kind === "bundle" ? (
    <div ref={ref}>{doc.docs.map((d, i) => <div key={d.ref || d.id || i} style={{ breakAfter: i < doc.docs.length - 1 ? "page" : "auto" }}>{renderDoc(d)}</div>)}</div>
  ) : renderDoc(doc, ref)}</div>;

  return { exportPdf, exportMany, pdfFileMany, sharePdf, pdfBase64, print, portal, doc };
}
